import type { MetricThresholdGroup } from '@flowqa/shared';

export type MetricThresholdKey =
  | 'passRate'
  | 'frequency'
  | 'avgDuration'
  | 'flakeScore'
  | 'mttf'
  | 'greenTime';

export type ThresholdDraft = { target: string; warn: string };

export type FrequencyUnit = 'hours' | 'days' | 'weeks' | 'months';
export type MttfUnit = 'minutes' | 'hours' | 'days';
export type AvgDurationUnit = 'seconds' | 'minutes' | 'hours';
export type GreenTimeUnit = 'hours' | 'days';

export type PreferredUnits = {
  frequencyTarget: FrequencyUnit;
  frequencyWarn: FrequencyUnit;
  mttfTarget: MttfUnit;
  mttfWarn: MttfUnit;
  avgDurationTarget: AvgDurationUnit;
  avgDurationWarn: AvgDurationUnit;
  greenTimeTarget: GreenTimeUnit;
  greenTimeWarn: GreenTimeUnit;
};

export type FrequencyThresholdDraft = {
  targetValue: string;
  targetUnit: FrequencyUnit;
  warnValue: string;
  warnUnit: FrequencyUnit;
};

export type MttfThresholdDraft = {
  targetValue: string;
  targetUnit: MttfUnit;
  warnValue: string;
  warnUnit: MttfUnit;
};

export type MetricThresholdDrafts = Omit<
  Record<MetricThresholdKey, ThresholdDraft>,
  'frequency' | 'mttf' | 'avgDuration' | 'greenTime'
> & {
  frequency: FrequencyThresholdDraft;
  mttf: MttfThresholdDraft;
  avgDuration: {
    targetValue: string;
    targetUnit: AvgDurationUnit;
    warnValue: string;
    warnUnit: AvgDurationUnit;
  };
  greenTime: {
    targetValue: string;
    targetUnit: GreenTimeUnit;
    warnValue: string;
    warnUnit: GreenTimeUnit;
  };
};

export const metricThresholdKeys: MetricThresholdKey[] = [
  'passRate',
  'frequency',
  'avgDuration',
  'flakeScore',
  'mttf',
  'greenTime',
];

export const frequencyUnitOptions: Array<{ value: FrequencyUnit; label: string }> = [
  { value: 'hours', label: 'Hour' },
  { value: 'days', label: 'Day' },
  { value: 'weeks', label: 'Week' },
  { value: 'months', label: 'Month' },
];

export const mttfUnitOptions: Array<{ value: MttfUnit; label: string }> = [
  { value: 'minutes', label: 'Minute' },
  { value: 'hours', label: 'Hour' },
  { value: 'days', label: 'Day' },
];

export const avgDurationUnitOptions: Array<{ value: AvgDurationUnit; label: string }> = [
  { value: 'seconds', label: 'Second' },
  { value: 'minutes', label: 'Minute' },
  { value: 'hours', label: 'Hour' },
];

export const greenTimeUnitOptions: Array<{ value: GreenTimeUnit; label: string }> = [
  { value: 'hours', label: 'Hour' },
  { value: 'days', label: 'Day' },
];

const toFrequencyUnit = (valuePerDay: number): FrequencyUnit => {
  if (valuePerDay >= 24) return 'hours';
  if (valuePerDay >= 1) return 'days';
  if (valuePerDay >= 1 / 7) return 'weeks';
  return 'months';
};

const formatDraftNumber = (value: number): string => {
  const nearestInteger = Math.round(value);
  if (Math.abs(value - nearestInteger) < 1e-8) {
    return nearestInteger.toString();
  }

  return Number(value.toFixed(12)).toString();
};

const toFrequencyValue = (valuePerDay: number, unit: FrequencyUnit): string => {
  if (unit === 'months') return formatDraftNumber(valuePerDay * 30);
  if (unit === 'weeks') return formatDraftNumber(valuePerDay * 7);
  if (unit === 'days') return formatDraftNumber(valuePerDay);
  return formatDraftNumber(valuePerDay / 24);
};

const isFrequencyUnit = (value: string | undefined): value is FrequencyUnit =>
  value === 'hours' || value === 'days' || value === 'weeks' || value === 'months';

const isAvgDurationUnit = (value: string | undefined): value is AvgDurationUnit =>
  value === 'seconds' || value === 'minutes' || value === 'hours';

export const buildThresholdDrafts = (
  thresholds: MetricThresholdGroup,
  preferredUnits?: {
    frequencyTarget?: FrequencyUnit;
    frequencyWarn?: FrequencyUnit;
    mttfTarget?: MttfUnit;
    mttfWarn?: MttfUnit;
    avgDurationTarget?: AvgDurationUnit;
    avgDurationWarn?: AvgDurationUnit;
    greenTimeTarget?: GreenTimeUnit;
    greenTimeWarn?: GreenTimeUnit;
  },
): MetricThresholdDrafts => {
  const frequencyTargetUnit =
    preferredUnits?.frequencyTarget ??
    (isFrequencyUnit(thresholds.frequency.targetUnit)
      ? thresholds.frequency.targetUnit
      : undefined) ??
    toFrequencyUnit(thresholds.frequency.target);
  const frequencyWarnUnit =
    preferredUnits?.frequencyWarn ??
    (isFrequencyUnit(thresholds.frequency.warnUnit) ? thresholds.frequency.warnUnit : undefined) ??
    toFrequencyUnit(thresholds.frequency.warn);
  const mttfTargetUnit = preferredUnits?.mttfTarget ?? 'hours';
  const mttfWarnUnit = preferredUnits?.mttfWarn ?? 'hours';
  const avgDurationTargetUnit =
    preferredUnits?.avgDurationTarget ??
    (isAvgDurationUnit(thresholds.avgDuration.targetUnit)
      ? thresholds.avgDuration.targetUnit
      : undefined) ??
    'seconds';
  const avgDurationWarnUnit =
    preferredUnits?.avgDurationWarn ??
    (isAvgDurationUnit(thresholds.avgDuration.warnUnit)
      ? thresholds.avgDuration.warnUnit
      : undefined) ??
    'seconds';
  const greenTimeTargetUnit = preferredUnits?.greenTimeTarget ?? 'days';
  const greenTimeWarnUnit = preferredUnits?.greenTimeWarn ?? 'days';

  return {
    passRate: {
      target: thresholds.passRate.target.toString(),
      warn: thresholds.passRate.warn.toString(),
    },
    frequency: {
      targetValue: toFrequencyValue(thresholds.frequency.target, frequencyTargetUnit),
      targetUnit: frequencyTargetUnit,
      warnValue: toFrequencyValue(thresholds.frequency.warn, frequencyWarnUnit),
      warnUnit: frequencyWarnUnit,
    },
    avgDuration: {
      targetValue: (avgDurationTargetUnit === 'hours'
        ? thresholds.avgDuration.target / 3600
        : avgDurationTargetUnit === 'minutes'
          ? thresholds.avgDuration.target / 60
          : thresholds.avgDuration.target
      ).toString(),
      targetUnit: avgDurationTargetUnit,
      warnValue: (avgDurationWarnUnit === 'hours'
        ? thresholds.avgDuration.warn / 3600
        : avgDurationWarnUnit === 'minutes'
          ? thresholds.avgDuration.warn / 60
          : thresholds.avgDuration.warn
      ).toString(),
      warnUnit: avgDurationWarnUnit,
    },
    flakeScore: {
      target: thresholds.flakeScore.target.toString(),
      warn: thresholds.flakeScore.warn.toString(),
    },
    mttf: {
      targetValue: (mttfTargetUnit === 'days'
        ? thresholds.mttf.target / 24
        : mttfTargetUnit === 'minutes'
          ? thresholds.mttf.target * 60
          : thresholds.mttf.target
      ).toString(),
      targetUnit: mttfTargetUnit,
      warnValue: (mttfWarnUnit === 'days'
        ? thresholds.mttf.warn / 24
        : mttfWarnUnit === 'minutes'
          ? thresholds.mttf.warn * 60
          : thresholds.mttf.warn
      ).toString(),
      warnUnit: mttfWarnUnit,
    },
    greenTime: {
      targetValue: (greenTimeTargetUnit === 'hours'
        ? thresholds.greenTime.target * 24
        : thresholds.greenTime.target
      ).toString(),
      targetUnit: greenTimeTargetUnit,
      warnValue: (greenTimeWarnUnit === 'hours'
        ? thresholds.greenTime.warn * 24
        : thresholds.greenTime.warn
      ).toString(),
      warnUnit: greenTimeWarnUnit,
    },
  };
};

export const parseThresholdValue = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  if (parsed < 0) return null;
  return parsed;
};

export const parseFrequencyPerDay = (value: string, unit: FrequencyUnit): number | null => {
  const parsed = parseThresholdValue(value);
  if (parsed === null) return null;
  if (parsed === 0) return null;
  if (unit === 'months') return parsed / 30;
  if (unit === 'weeks') return parsed / 7;
  if (unit === 'days') return parsed;
  return parsed * 24;
};

export const parseMttfHours = (value: string, unit: MttfUnit): number | null => {
  const parsed = parseThresholdValue(value);
  if (parsed === null) return null;
  if (unit === 'days') return parsed * 24;
  if (unit === 'minutes') return parsed / 60;
  return parsed;
};

export const parseAvgDurationSeconds = (value: string, unit: AvgDurationUnit): number | null => {
  const parsed = parseThresholdValue(value);
  if (parsed === null) return null;
  if (unit === 'hours') return parsed * 3600;
  if (unit === 'minutes') return parsed * 60;
  return parsed;
};

export const parseGreenTimeDays = (value: string, unit: GreenTimeUnit): number | null => {
  const parsed = parseThresholdValue(value);
  if (parsed === null) return null;
  if (unit === 'hours') return parsed / 24;
  return parsed;
};

export const getPreferredUnits = (drafts: MetricThresholdDrafts): PreferredUnits => ({
  frequencyTarget: drafts.frequency.targetUnit,
  frequencyWarn: drafts.frequency.warnUnit,
  mttfTarget: drafts.mttf.targetUnit,
  mttfWarn: drafts.mttf.warnUnit,
  avgDurationTarget: drafts.avgDuration.targetUnit,
  avgDurationWarn: drafts.avgDuration.warnUnit,
  greenTimeTarget: drafts.greenTime.targetUnit,
  greenTimeWarn: drafts.greenTime.warnUnit,
});

export const isThresholdsDirty = (
  drafts: MetricThresholdDrafts,
  thresholds: MetricThresholdGroup,
): boolean =>
  metricThresholdKeys.some((key) => {
    if (key === 'frequency') {
      const target = parseFrequencyPerDay(
        drafts.frequency.targetValue,
        drafts.frequency.targetUnit,
      );
      const warn = parseFrequencyPerDay(drafts.frequency.warnValue, drafts.frequency.warnUnit);
      if (target === null || warn === null) return true;
      const baseTargetUnit = isFrequencyUnit(thresholds.frequency.targetUnit)
        ? thresholds.frequency.targetUnit
        : toFrequencyUnit(thresholds.frequency.target);
      const baseWarnUnit = isFrequencyUnit(thresholds.frequency.warnUnit)
        ? thresholds.frequency.warnUnit
        : toFrequencyUnit(thresholds.frequency.warn);
      return (
        target !== thresholds.frequency.target ||
        warn !== thresholds.frequency.warn ||
        drafts.frequency.targetUnit !== baseTargetUnit ||
        drafts.frequency.warnUnit !== baseWarnUnit
      );
    }
    if (key === 'mttf') {
      const target = parseMttfHours(drafts.mttf.targetValue, drafts.mttf.targetUnit);
      const warn = parseMttfHours(drafts.mttf.warnValue, drafts.mttf.warnUnit);
      if (target === null || warn === null) return true;
      return target !== thresholds.mttf.target || warn !== thresholds.mttf.warn;
    }
    if (key === 'avgDuration') {
      const target = parseAvgDurationSeconds(
        drafts.avgDuration.targetValue,
        drafts.avgDuration.targetUnit,
      );
      const warn = parseAvgDurationSeconds(
        drafts.avgDuration.warnValue,
        drafts.avgDuration.warnUnit,
      );
      if (target === null || warn === null) return true;
      const baseTargetUnit = isAvgDurationUnit(thresholds.avgDuration.targetUnit)
        ? thresholds.avgDuration.targetUnit
        : 'seconds';
      const baseWarnUnit = isAvgDurationUnit(thresholds.avgDuration.warnUnit)
        ? thresholds.avgDuration.warnUnit
        : 'seconds';
      return (
        target !== thresholds.avgDuration.target ||
        warn !== thresholds.avgDuration.warn ||
        drafts.avgDuration.targetUnit !== baseTargetUnit ||
        drafts.avgDuration.warnUnit !== baseWarnUnit
      );
    }
    if (key === 'greenTime') {
      const target = parseGreenTimeDays(drafts.greenTime.targetValue, drafts.greenTime.targetUnit);
      const warn = parseGreenTimeDays(drafts.greenTime.warnValue, drafts.greenTime.warnUnit);
      if (target === null || warn === null) return true;
      return target !== thresholds.greenTime.target || warn !== thresholds.greenTime.warn;
    }
    const draft = drafts[key];
    const target = parseThresholdValue(draft.target);
    const warn = parseThresholdValue(draft.warn);
    if (target === null || warn === null) return true;
    return target !== thresholds[key].target || warn !== thresholds[key].warn;
  });

export const isThresholdsValid = (drafts: MetricThresholdDrafts): boolean =>
  metricThresholdKeys.every((key) => {
    if (key === 'frequency') {
      return (
        parseFrequencyPerDay(drafts.frequency.targetValue, drafts.frequency.targetUnit) !== null &&
        parseFrequencyPerDay(drafts.frequency.warnValue, drafts.frequency.warnUnit) !== null
      );
    }
    if (key === 'mttf') {
      return (
        parseMttfHours(drafts.mttf.targetValue, drafts.mttf.targetUnit) !== null &&
        parseMttfHours(drafts.mttf.warnValue, drafts.mttf.warnUnit) !== null
      );
    }
    if (key === 'avgDuration') {
      return (
        parseAvgDurationSeconds(drafts.avgDuration.targetValue, drafts.avgDuration.targetUnit) !==
          null &&
        parseAvgDurationSeconds(drafts.avgDuration.warnValue, drafts.avgDuration.warnUnit) !== null
      );
    }
    if (key === 'greenTime') {
      return (
        parseGreenTimeDays(drafts.greenTime.targetValue, drafts.greenTime.targetUnit) !== null &&
        parseGreenTimeDays(drafts.greenTime.warnValue, drafts.greenTime.warnUnit) !== null
      );
    }
    const draft = drafts[key];
    return parseThresholdValue(draft.target) !== null && parseThresholdValue(draft.warn) !== null;
  });
